import type { GenericRecordData, RecordShape } from '@lemonade-hq/maschema-schema';
import type { FlatValidationsTree } from '@lemonade-hq/maschema-validations';
import { Env, useInfraContext } from '@lemonade-hq/nuiza';
import type { UniclientAction } from '@lemonade-hq/uniclient-chat';
import { useEffect } from 'react';
import './uniclientExtension.css';
import { useLocation } from 'react-router-dom';

interface UseUniclientExtensionProps {
  readonly name?: string;
  readonly failingValidations?: FlatValidationsTree;
  readonly steps?: { readonly id: string; readonly name: string }[];
  readonly answersSchema?: RecordShape;
  readonly persistedAnswersByStep?: GenericRecordData[];
  readonly currentStepAnswers?: GenericRecordData;
  readonly uiAttrs?: GenericRecordData;
  readonly currentStepIndex?: number;
}

interface MessageHandlers {
  readonly postMessage: (message: Record<string, unknown>) => void;
  readonly onConnection: (callback: (payload: unknown) => unknown) => () => void;
  readonly onAction: (callback: (action?: UniclientAction) => Promise<unknown>) => () => void;
}

let messageHandlers: MessageHandlers | null = null;
export const setUniclientExtensionMessageHandlers = async (): Promise<void> => {
  messageHandlers = await import('./messageHandler');
};

export const useUniclientExtension = (
  {
    failingValidations,
    steps,
    answersSchema,
    persistedAnswersByStep,
    currentStepIndex,
    currentStepAnswers,
    uiAttrs,
    name,
  }: UseUniclientExtensionProps,
  {
    prefix,
    onAction,
  }: {
    readonly prefix?: string;
    readonly onAction?: (action?: UniclientAction) => Promise<unknown>;
  } = {},
): void => {
  const { env } = useInfraContext();
  const location = useLocation();

  useEffect(() => {
    if (steps == null) return;

    const currentStepName = steps[currentStepIndex ?? 0]?.name ?? '';
    const searchParams = new URLSearchParams(location.search);
    if (env !== Env.Production && searchParams.get('debug') === 'true' && Boolean(currentStepName)) {
      document.body.dataset.step = currentStepName;
    }

    return () => {
      delete document.body.dataset.step;
    };
  }, [env, location.search, steps, currentStepIndex]);

  useEffect(() => {
    if (env === Env.Production) return;

    void setUniclientExtensionMessageHandlers();
  }, [env]);

  useEffect(() => {
    if (env === Env.Production) return;

    const postMessage = (): void => {
      const message = {
        failingValidations,
        steps,
        answersSchema,
        persistedAnswersByStep,
        currentStepIndex,
        currentStepAnswers,
        uiAttrs,
        name,
      };
      messageHandlers?.postMessage(prefix != null ? { [prefix]: message } : message);
    };

    let abortConnectionListen: (() => void) | undefined;
    let abortActionListen: (() => void) | undefined;
    let interval: NodeJS.Timeout | undefined;

    if (messageHandlers != null) {
      abortConnectionListen = messageHandlers.onConnection(postMessage);
      if (onAction != null) {
        abortActionListen = messageHandlers.onAction(onAction);
      }

      postMessage();
    } else {
      let attempts = 0;
      interval = setInterval(() => {
        if (messageHandlers != null) {
          clearInterval(interval);
          abortConnectionListen = messageHandlers.onConnection(postMessage);

          if (onAction != null) {
            abortActionListen = messageHandlers.onAction(onAction);
          }

          postMessage();
        } else if (attempts > 20) {
          clearInterval(interval);
        }

        attempts++;
      }, 100);
    }

    return () => {
      abortConnectionListen?.();
      abortActionListen?.();
      clearInterval(interval);
      if (prefix != null) {
        messageHandlers?.postMessage({ [prefix]: null });
      }
    };
  }, [
    failingValidations,
    steps,
    answersSchema,
    persistedAnswersByStep,
    currentStepIndex,
    currentStepAnswers,
    uiAttrs,
    prefix,
    name,
    env,
    onAction,
  ]);
};
